import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Turnstile } from "@marsidev/react-turnstile";
import {
  CheckCircle,
  Key,
  XCircle,
  AlertCircle,
  Clock,
  LogOut,
  CreditCard,
  Sparkles,
  Church,
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { logger } from "../../lib/logger";
import { useAuth } from "../../hooks/useAuth";
import { useSubscription } from "../../hooks/useSubscription";
import { useAdmin } from "../../hooks/useAdmin";
import { getActivePriceDisplay, getActivePrice } from "../../stripe-config";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as
  | string
  | undefined;

// ── Ministry profile constants ─────────────────────────────────────────────────

const MINISTRY_ROLES = [
  'Senior / Lead Pastor', 'Associate Pastor', 'Youth Pastor',
  "Children's Ministry Leader", 'Bible Study Leader', 'Worship Leader',
  'Chaplain', 'Seminary Student', 'Lay Preacher / Evangelist', 'Personal Study',
];

const DENOMINATIONS = [
  'Baptist', 'Reformed / Presbyterian', 'Methodist / Wesleyan', 'Lutheran',
  'Anglican / Episcopal', 'Pentecostal / Charismatic', 'Non-denominational',
  'Catholic', 'Other',
];

const CONGREGATION_SIZES = ['Under 50', '50–150', '150–500', '500–1,000', '1,000+'];

const USE_CASES: { value: string; label: string }[] = [
  { value: 'sunday_sermons',         label: 'Sunday Sermons'      },
  { value: 'midweek_bible_study',    label: 'Midweek Bible Study' },
  { value: 'youth_ministry',         label: 'Youth Ministry'      },
  { value: 'small_groups',           label: 'Small Groups'        },
  { value: 'personal_study',         label: 'Personal Study'      },
  { value: 'sermon_series_planning', label: 'Series Planning'     },
];

export function AccountSettings() {
  const { user, loading: authLoading } = useAuth();
  const { subscription, stripeSubscription, loading: subscriptionLoading, refresh } = useSubscription();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const navigate = useNavigate();

  const [profile, setProfile] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
  });

  const [ministry, setMinistry] = useState({
    role: "",
    denomination: "",
    denominationOther: "",
    congregationSize: "",
    useCases: [] as string[],
  });
  const [savingMinistry, setSavingMinistry] = useState(false);
  const [ministrySuccess, setMinistrySuccess] = useState(false);

  const [saving, setSaving] = useState(false);
  const [resetCaptchaVisible, setResetCaptchaVisible] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedInterval, setSelectedInterval] = useState<
    "monthly" | "annual"
  >("monthly");

  useEffect(() => {
    if (user) {
      setProfile({
        first_name: user.user_metadata?.first_name || "",
        last_name:
          user.user_metadata?.last_name || user.user_metadata?.surname || "",
        phone: user.user_metadata?.phone || "",
        email: user.email || "",
      });

      // Load ministry profile fields
      supabase
        .from("profiles")
        .select("ministry_role, denomination, congregation_size, primary_use_cases")
        .eq("id", user.id)
        .single()
        .then(({ data }) => {
          if (!data) return;
          const denom: string = data.denomination || "";
          const isOther = denom && !DENOMINATIONS.filter(d => d !== "Other").includes(denom);
          setMinistry({
            role: data.ministry_role || "",
            denomination: isOther ? "Other" : denom,
            denominationOther: isOther ? denom.replace(/^Other:\s*/i, "") : "",
            congregationSize: data.congregation_size || "",
            useCases: data.primary_use_cases || [],
          });
        });
    }
  }, [user]);

  // Refresh subscription data on mount to detect any admin changes
  useEffect(() => {
    if (user && refresh) {
      refresh();
    }
  }, [user, refresh]);

  const handleProfileUpdate = (field: string, value: string) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      setError(null);

      const updates = {
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone,
        full_name: `${profile.first_name} ${profile.last_name}`.trim(),
      };

      const { error } = await supabase.auth.updateUser({
        data: updates
      });

      if (error) throw error;

      // Also update the profiles table if it exists
      await supabase
        .from("profiles")
        .upsert({ 
          id: user?.id, 
          ...updates 
        }, { 
          onConflict: 'id' 
        });

      setSuccess("Profile updated successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const saveMinistryProfile = async () => {
    if (!user) return;
    setSavingMinistry(true);
    try {
      const denominationValue =
        ministry.denomination === "Other"
          ? ministry.denominationOther.trim()
            ? `Other: ${ministry.denominationOther.trim()}`
            : "Other"
          : ministry.denomination;

      const { error } = await supabase.from("profiles").upsert({
        id: user.id,
        ministry_role: ministry.role || null,
        denomination: denominationValue || null,
        congregation_size: ministry.congregationSize || null,
        primary_use_cases: ministry.useCases.length > 0 ? ministry.useCases : null,
        onboarding_completed_at: new Date().toISOString(),
        onboarding_skipped_at: null,
      }, { onConflict: "id" });

      if (error) throw error;
      setMinistrySuccess(true);
      setTimeout(() => setMinistrySuccess(false), 3000);
    } catch (err: any) {
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSavingMinistry(false);
    }
  };

  const startCheckout = async () => {
    try {
      setSaving(true);
      setError(null);

      const activePrice = getActivePrice(selectedInterval as 'monthly' | 'annual');
      const { data, error } = await supabase.functions.invoke(
        "create-checkout-session",
        {
          body: { 
            tier: "monthly",
            interval: selectedInterval,
            email: user?.email,
            userId: user?.id,
            priceId: activePrice.priceId
          },
        },
      );

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (err: any) {
      console.error("Checkout error:", err);
      setError(err.message || "Failed to start checkout.");
    } finally {
      setSaving(false);
    }
  };

  const sendPasswordReset = async (captchaToken?: string) => {
    try {
      setSaving(true);
      setError(null);

      const { error } = await supabase.auth.resetPasswordForEmail(
        profile.email,
        {
          redirectTo: `${window.location.origin}/auth/reset-password`,
          ...(captchaToken ? { captchaToken } : {}),
        },
      );

      if (error) throw error;
      setResetCaptchaVisible(false);
      setSuccess("Password reset email sent. Check your inbox.");
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      // Tear the widget down rather than resetting it in place. A Turnstile
      // token is single-use, so a retry does need a fresh one — but resetting
      // here re-fires onSuccess, which calls straight back into this function
      // and spins forever against a persistent error like a rate limit.
      // Unmounting means the next click mounts a clean widget instead.
      setResetCaptchaVisible(false);
      setError(err.message);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    // Supabase's recover endpoint is captcha-gated once project-level
    // protection is on, and it does not care that the caller is already
    // signed in. Reveal the widget and send from its onSuccess so the request
    // carries a token; with no site key configured this degrades to the
    // previous straight-through behaviour.
    if (TURNSTILE_SITE_KEY) {
      setError(null);
      setResetCaptchaVisible(true);
      return;
    }

    await sendPasswordReset();
  };

  const handleDeleteAccount = async () => {
    try {
      setDeletingAccount(true);
      setError(null);

      // SermonBuildNew's `close-account` function, not `delete_own_account`: it cancels the Stripe
      // subscription (base and Studio) before anything is deleted, so a closed account is never left
      // being charged, and it can close an account that has used Studio, which the old RPC cannot.
      // Its refusals carry their own plain message in the response body.
      const { data, error } = await supabase.functions.invoke('close-account', { body: {} });

      if (error) {
        let message = '';
        try {
          const body = await (error as { context?: Response }).context?.json();
          message = typeof body?.error === 'string' ? body.error : '';
        } catch {
          // No readable body: fall through to the generic message.
        }
        throw new Error(message || 'Could not reach the server just now.');
      }
      if (!data?.closed) throw new Error('The server did not confirm the account was closed.');

      // Deletion succeeded — sign out and hard-redirect so no stale session lingers.
      setConfirmingDelete(false);
      await supabase.auth.signOut();
      window.location.href = "/auth";
    } catch (err: any) {
      // Keep the failure visible (no auto-dismiss) and leave the confirm panel
      // open so a failed deletion can't masquerade as success.
      logger.error("Account deletion failed:", err);
      setDeletingAccount(false);
      setError(
        err?.message
          ? `Account deletion failed — your account was NOT deleted: ${err.message}`
          : "Account deletion failed — your account was NOT deleted. Please try again or contact support."
      );
    }
  };

  const handleLogoutAllDevices = async () => {
    try {
      setLoggingOut(true);
      setError(null);

      const { error } = await supabase.auth.signOut({ scope: "global" });
      if (error) throw error;

      navigate("/auth");
    } catch (err: any) {
      setError("Failed to log out from all devices");
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoggingOut(false);
    }
  };

  if (authLoading || subscriptionLoading || adminLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // Get subscription status
  const hasActiveSubscription = stripeSubscription?.subscription_status === 'active' || 
                                stripeSubscription?.subscription_status === 'trialing';
  const hasLifetimeLicense = stripeSubscription?.subscription_status === 'completed';
  // A "free premium" account is one granted complimentary access in user_subscriptions
  // with NO paid Stripe subscription behind it. Paying/lifetime customers must fall
  // through to the "Active Subscription" / "Lifetime License" labels instead.
  const hasFreePremiun = subscription?.active === true &&
    (subscription?.expires_at === null || new Date(subscription.expires_at) > new Date()) &&
    !hasActiveSubscription &&
    !hasLifetimeLicense;
  const isInTrial = subscription?.isInTrial;
  const daysLeftInTrial = subscription?.daysLeftInTrial || 0;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Success/Error Messages */}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
            <span className="text-green-800">{success}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Trial Status */}
      {isInTrial && !hasFreePremiun && (
        <div className="p-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start">
            <Clock className="h-6 w-6 text-yellow-600 mr-3 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                Free Trial Period
              </h3>
              <p className="text-yellow-700 mb-4">
                You have <strong>{daysLeftInTrial} days</strong> left in your free trial. 
                Your trial ends on {subscription?.trialEndsAt 
                  ? new Date(subscription.trialEndsAt).toLocaleDateString() 
                  : 'soon'}.
              </p>
              <div className="bg-yellow-100 p-3 rounded mb-4">
                <p className="text-sm text-yellow-800">
                  ⚠️ Access to all features will be discontinued after your trial expires unless you subscribe.
                </p>
              </div>

              {getActivePriceDisplay().isFounders && (
                <div className="bg-gradient-to-r from-gold/10 to-yellow-50 border border-gold/30 rounded-lg p-4 mb-4">
                  <div className="flex items-center mb-1">
                    <Sparkles className="h-4 w-4 text-gold mr-2" />
                    <span className="font-semibold text-midnight text-sm">Founders Pricing — Limited Time!</span>
                  </div>
                  <p className="text-xs text-slate">Lock in this special rate for the lifetime of your subscription. Price goes up to ${getActivePriceDisplay().regularMonthly}/month after the beta period ends.</p>
                </div>
              )}

              {/* Subscription Options */}
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="interval"
                      value="monthly"
                      checked={selectedInterval === "monthly"}
                      onChange={(e) => setSelectedInterval(e.target.value as "monthly")}
                      className="mr-2"
                    />
                    <span className="text-yellow-800">
                      Monthly - ${getActivePriceDisplay().monthly}/month
                      {getActivePriceDisplay().isFounders && <span className="text-xs line-through opacity-60 ml-1">${getActivePriceDisplay().regularMonthly}</span>}
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="interval"
                      value="annual"
                      checked={selectedInterval === "annual"}
                      onChange={(e) => setSelectedInterval(e.target.value as "annual")}
                      className="mr-2"
                    />
                    <span className="text-yellow-800">
                      Annual - ${getActivePriceDisplay().annual}/year (Save {getActivePriceDisplay().savingsPercent}%)
                      {getActivePriceDisplay().isFounders && <span className="text-xs line-through opacity-60 ml-1">${getActivePriceDisplay().regularAnnual}</span>}
                    </span>
                  </label>
                </div>

                <button
                  onClick={startCheckout}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <CreditCard className="h-5 w-5 mr-2" />
                  {saving ? "Processing..." : `Subscribe ${selectedInterval === "monthly" ? "Monthly" : "Annually"}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Status for Active Users */}
      {(hasActiveSubscription || hasLifetimeLicense || hasFreePremiun) && (
        <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-green-800">
                {hasLifetimeLicense 
                  ? "Lifetime License" 
                  : hasFreePremiun 
                    ? "Free Premium Account" 
                    : "Active Subscription"}
              </h3>
              <p className="text-green-700">
                {hasFreePremiun 
                  ? "You have been granted complimentary access to all SermonBuild features."
                  : "You have full access to all SermonBuild features."}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Profile Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center mb-6">
          <div className="p-2 bg-blue-100 rounded-lg mr-3">
            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Profile Information</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={profile.first_name}
              onChange={(e) => handleProfileUpdate("first_name", e.target.value)}
              placeholder="First Name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={profile.last_name}
              onChange={(e) => handleProfileUpdate("last_name", e.target.value)}
              placeholder="Last Name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
              value={profile.email}
              disabled
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone
            </label>
            <input
              type="tel"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={profile.phone}
              onChange={(e) => handleProfileUpdate("phone", e.target.value)}
              placeholder="Phone"
            />
          </div>
        </div>

        <button
          onClick={saveProfile}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving..." : "Save Profile"}
        </button>
      </div>

      {/* Ministry Profile */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center mb-6">
          <div className="p-2 bg-electric/10 rounded-lg mr-3">
            <Church className="h-6 w-6 text-electric" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Ministry Profile</h2>
            <p className="text-sm text-gray-500 mt-0.5">Used to personalise AI content for your tradition and context.</p>
          </div>
        </div>

        <div className="space-y-5">
          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Your Role</label>
            <select
              value={ministry.role}
              onChange={e => setMinistry(m => ({ ...m, role: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-electric/40 focus:border-electric bg-white text-gray-900"
            >
              <option value="">Select your role…</option>
              {MINISTRY_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          {/* Denomination */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Church Tradition</label>
            <div className="flex flex-wrap gap-2">
              {DENOMINATIONS.map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setMinistry(m => ({ ...m, denomination: d }))}
                  className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-all ${
                    ministry.denomination === d
                      ? "border-electric bg-electric text-white"
                      : "border-gray-300 text-gray-700 hover:border-electric/50"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
            {ministry.denomination === "Other" && (
              <input
                type="text"
                placeholder="Your tradition (optional)"
                value={ministry.denominationOther}
                onChange={e => setMinistry(m => ({ ...m, denominationOther: e.target.value }))}
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-electric/40 focus:border-electric text-gray-900"
              />
            )}
          </div>

          {/* Congregation size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Congregation Size</label>
            <div className="flex flex-wrap gap-2">
              {CONGREGATION_SIZES.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setMinistry(m => ({ ...m, congregationSize: s }))}
                  className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-all ${
                    ministry.congregationSize === s
                      ? "border-electric bg-electric text-white"
                      : "border-gray-300 text-gray-700 hover:border-electric/50"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Use cases */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Primary Use Cases</label>
            <div className="flex flex-wrap gap-2">
              {USE_CASES.map(uc => {
                const selected = ministry.useCases.includes(uc.value);
                return (
                  <button
                    key={uc.value}
                    type="button"
                    onClick={() => setMinistry(m => ({
                      ...m,
                      useCases: selected
                        ? m.useCases.filter(u => u !== uc.value)
                        : [...m.useCases, uc.value],
                    }))}
                    className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-all ${
                      selected
                        ? "border-electric bg-electric text-white"
                        : "border-gray-300 text-gray-700 hover:border-electric/50"
                    }`}
                  >
                    {uc.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-6">
          <button
            onClick={saveMinistryProfile}
            disabled={savingMinistry}
            className="bg-electric hover:bg-electric/90 text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {savingMinistry ? "Saving…" : "Save Ministry Profile"}
          </button>
          {ministrySuccess && (
            <span className="flex items-center gap-1.5 text-sm text-green-700">
              <CheckCircle className="h-4 w-4" /> Saved
            </span>
          )}
        </div>
      </div>

      {/* Account Security - Always visible to all users */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center mb-6">
          <div className="p-2 bg-green-100 rounded-lg mr-3">
            <Key className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Account Security</h2>
        </div>

        <div className="space-y-4">
          <div className="space-y-3">
            <button
              onClick={handlePasswordReset}
              disabled={saving || resetCaptchaVisible}
              className="w-full md:w-auto bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center md:justify-start"
            >
              <Key className="h-4 w-4 mr-2" />
              {saving
                ? "Sending..."
                : resetCaptchaVisible
                  ? "Verifying..."
                  : "Reset Password"}
            </button>

            {TURNSTILE_SITE_KEY && resetCaptchaVisible && (
              <Turnstile
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={(token) => sendPasswordReset(token)}
                onError={() => {
                  setResetCaptchaVisible(false);
                  setError("Verification failed. Please try again.");
                  setTimeout(() => setError(null), 5000);
                }}
                onExpire={() => setResetCaptchaVisible(false)}
                options={{ theme: "light", size: "normal" }}
              />
            )}
          </div>

          <button
            onClick={handleLogoutAllDevices}
            disabled={loggingOut}
            className="w-full md:w-auto bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center md:justify-start"
          >
            <LogOut className="h-4 w-4 mr-2" />
            {loggingOut ? "Logging out..." : "Log out from all devices"}
          </button>
        </div>
      </div>

      {/* Danger Zone - Always visible to all users */}
      <div className="bg-white border border-red-200 rounded-lg p-6">
        <div className="flex items-center mb-6">
          <div className="p-2 bg-red-100 rounded-lg mr-3">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-red-900">Danger Zone</h2>
        </div>

        <div className="space-y-4">
          <button
            onClick={() => setConfirmingDelete(!confirmingDelete)}
            disabled={deletingAccount}
            className={`w-full md:w-auto px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center md:justify-start ${
              confirmingDelete 
                ? "bg-red-600 hover:bg-red-700 text-white" 
                : "bg-white border border-red-300 hover:bg-red-50 text-red-700"
            }`}
          >
            <XCircle className="h-4 w-4 mr-2" />
            {deletingAccount
              ? "Deleting..."
              : confirmingDelete
                ? "Confirm Delete Account"
                : "Delete Account"}
          </button>

          {confirmingDelete && (
            <div className="p-4 border border-red-300 bg-red-50 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-800 mb-3">
                    This will permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={deletingAccount}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deletingAccount ? "Deleting..." : "Yes, delete my account"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}